import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonSize = "small" | "middle" | "large";

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "color"> & {
  htmlType?: "button" | "submit" | "reset";
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean | { delay?: number };
  danger?: boolean;
  block?: boolean;
  ghost?: boolean;
  shape?: "default" | "circle" | "round";
  color?: "default" | "primary" | "danger";
  variant?: "outlined" | "dashed" | "solid" | "filled" | "text" | "link";
};

const mergeClassNames = (...classes: Array<string | undefined | false>): string => classes.filter(Boolean).join(" ");

const SIZE_CLASS: Record<ButtonSize, string> = {
  small: "h-6 px-2 text-xs",
  middle: "h-8 px-3 text-sm",
  large: "h-10 px-4 text-base",
};

export const CommonButton = ({
  children,
  htmlType = "button",
  size = "small",
  icon,
  loading = false,
  danger = false,
  block = false,
  ghost = false,
  shape = "default",
  color,
  variant,
  className,
  disabled,
  ...props
}: ButtonProps) => {
  const isLoading = typeof loading === "boolean" ? loading : Boolean(loading);
  const isDanger = danger || color === "danger";

  return (
    <button
      {...props}
      type={htmlType}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={mergeClassNames(
        "ant-btn inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap border font-normal leading-none outline-none transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
        SIZE_CLASS[size],
        isDanger && (variant === "solid"
          ? "!border-red-500 !bg-red-500 !text-white hover:!border-red-400 hover:!bg-red-400"
          : "!border-red-300 !text-red-500 hover:!border-red-500 hover:!text-red-600"),
        ghost && "!bg-transparent",
        shape === "circle" ? "aspect-square !rounded-full !px-0" : shape === "round" ? "!rounded-full" : "rounded-none",
        block && "w-full",
        className,
      )}
    >
      {isLoading ? (
        <span className="inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
};

export const Button = CommonButton;
